/**
 * ESS POS — Main Application Entrypoint & Role-Based Workspace Router
 * Product: ESS POS | Company: Easy Smart System (ESS) | Tagline: Work Smarter
 */

import { apiClient } from './api/apiClient';
import { renderAppShell, WORKSPACE_META } from './components/appShell';
import { openModal, renderErrorState, showToast } from './components/ui';
import { AuthenticatedUser, WorkspaceId } from './types/contracts';
import { renderAuthScreen } from './workspaces/authView';
import { renderCashierWorkspace } from './workspaces/cashierWorkspace';
import { renderManagerWorkspace } from './workspaces/managerWorkspace';
import { renderOwnerWorkspace, renderSuperAdminWorkspace } from './workspaces/ownerAdminWorkspace';
import { renderStockKeeperWorkspace } from './workspaces/stockKeeperWorkspace';
import { renderWaiterWorkspace } from './workspaces/waiterWorkspace';

class EssPosApplication {
  private root: HTMLElement;
  private activeTabByWorkspace: Record<WorkspaceId, string> = {
    CASHIER: 'pos-sale',
    WAITER: 'tables',
    STOCK_KEEPER: 'dashboard',
    MANAGER: 'overview',
    OWNER: 'executive',
    SUPER_ADMIN: 'tenants',
  };
  private sidebarCollapsed = false;

  constructor(rootElement: HTMLElement) {
    this.root = rootElement;
    apiClient.onUnauthorized(() => {
      this.mount();
    });
    this.setupGlobalErrorBoundary();
  }

  private setupGlobalErrorBoundary() {
    window.addEventListener('error', (event) => {
      console.error('[ESS POS Error Boundary]', event.error || event.message);
    });
    window.addEventListener('unhandledrejection', (event) => {
      console.error('[ESS POS Unhandled Promise Rejection]', event.reason);
    });
  }

  public mount() {
    this.root.innerHTML = '';

    if (!apiClient.isAuthenticated()) {
      const authScreen = renderAuthScreen(() => {
        this.mount();
      });
      this.root.appendChild(authScreen);
      return;
    }

    const user = apiClient.getUser()!;
    let currentWorkspace = apiClient.getActiveWorkspace() || apiClient.resolvePrimaryWorkspace(user);
    const allowed = apiClient.getAuthorizedWorkspaces(user);
    if (!allowed.includes(currentWorkspace)) {
      currentWorkspace = allowed[0] || 'CASHIER';
      apiClient.setActiveWorkspace(currentWorkspace);
    }

    const activeTab =
      this.activeTabByWorkspace[currentWorkspace] ||
      WORKSPACE_META[currentWorkspace].navItems[0].id;

    const shell = renderAppShell({
      workspace: currentWorkspace,
      activeTab,
      sidebarCollapsed: this.sidebarCollapsed,
      onSelectTab: (tabId: string) => {
        this.activeTabByWorkspace[currentWorkspace] = tabId;
        this.mount();
      },
      onSwitchWorkspace: (nextWorkspace: WorkspaceId) => {
        // Strict Role & Account Isolation:
        // Switching to ANY other workspace requires entering the PIN for that role/user!
        if (!user.isSuperAdmin && nextWorkspace !== currentWorkspace) {
          promptWorkspaceSwitchPin(
            user,
            nextWorkspace,
            () => {
              apiClient.setActiveWorkspace(nextWorkspace);
              this.mount();
            },
            () => {
              this.mount();
            }
          );
          return;
        }

        apiClient.setActiveWorkspace(nextWorkspace);
        this.mount();
      },
      onToggleSidebar: () => {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        this.mount();
      },
      onLogout: () => {
        apiClient.logout();
        this.mount();
      },
    });

    this.root.appendChild(shell);

    const viewport = shell.querySelector('#ess-workspace-viewport') as HTMLElement | null;
    if (viewport) {
      this.renderWorkspaceContent(viewport, currentWorkspace, activeTab);
    }
  }

  private async renderWorkspaceContent(
    viewport: HTMLElement,
    workspace: WorkspaceId,
    activeTab: string
  ) {
    const navigateTab = (tabId: string) => {
      this.activeTabByWorkspace[workspace] = tabId;
      this.mount();
    };

    try {
      switch (workspace) {
        case 'CASHIER':
          await renderCashierWorkspace(viewport, activeTab, navigateTab);
          break;
        case 'WAITER':
          await renderWaiterWorkspace(viewport, activeTab, navigateTab);
          break;
        case 'STOCK_KEEPER':
          await renderStockKeeperWorkspace(viewport, activeTab, navigateTab);
          break;
        case 'MANAGER':
          await renderManagerWorkspace(viewport, activeTab, navigateTab);
          break;
        case 'OWNER':
          await renderOwnerWorkspace(viewport, activeTab, navigateTab);
          break;
        case 'SUPER_ADMIN':
          await renderSuperAdminWorkspace(viewport, activeTab, navigateTab);
          break;
      }
    } catch (err: any) {
      viewport.innerHTML = renderErrorState({
        title: 'Workspace Render Exception',
        message: err?.message || 'An unexpected error occurred in the workspace view.',
        retryButtonId: 'global-ws-retry',
      });
      viewport.querySelector('#global-ws-retry')?.addEventListener('click', () => {
        this.mount();
      });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const rootEl = document.getElementById('ess-app-root');
  if (rootEl) {
    const app = new EssPosApplication(rootEl);
    app.mount();
  }
});

/**
 * Floor Station Security Guard:
 * When switching between workspaces, require the target user or role's private PIN challenge.
 * A waiter cannot access cashier without a Cashier PIN.
 * A storekeeper cannot access waiter without a Waiter PIN.
 */
async function promptWorkspaceSwitchPin(
  currentUser: AuthenticatedUser,
  targetWorkspace: WorkspaceId,
  onAuthorized: () => void,
  onCancel: () => void
) {
  const tenant = apiClient.getTenant();
  const tenantId = tenant?.id || '';
  let staffList: any[] = [];
  try {
    staffList = await apiClient.stationStaffList(tenantId);
  } catch {
    try {
      staffList = await apiClient.listStaff();
    } catch {
      staffList = [];
    }
  }

  const roleNameMap: Record<WorkspaceId, string> = {
    CASHIER: 'Cashier',
    WAITER: 'Waiter',
    STOCK_KEEPER: 'Stock Keeper',
    MANAGER: 'Manager',
    OWNER: 'Restaurant Owner',
    SUPER_ADMIN: 'ESS Super Admin',
  };

  const requiredRoleMap: Record<WorkspaceId, string[]> = {
    CASHIER: ['CASHIER', 'MANAGER', 'OWNER'],
    WAITER: ['WAITER', 'MANAGER', 'OWNER'],
    STOCK_KEEPER: ['INVENTORY_CLERK', 'STOCK_KEEPER', 'MANAGER', 'OWNER'],
    MANAGER: ['MANAGER', 'OWNER'],
    OWNER: ['OWNER'],
    SUPER_ADMIN: ['SUPER_ADMIN'],
  };

  const allowedRoles = requiredRoleMap[targetWorkspace] || ['OWNER'];

  // Filter staff who have the role required for the target workspace
  const matchingStaff = staffList.filter((s) => {
    const roles = Array.isArray(s.roles)
      ? s.roles.map((r: any) => (typeof r === 'string' ? r : r.role).toUpperCase())
      : [];
    return roles.some((r: string) => allowedRoles.includes(r)) || s.isSuperAdmin;
  });

  const candidates = matchingStaff.length > 0 ? matchingStaff : [currentUser];
  let selectedUserId = candidates.find((c) => c.id === currentUser.id)?.id || candidates[0]?.id || currentUser.id;
  let enteredPin = '';
  let isSubmitting = false;

  const targetName = roleNameMap[targetWorkspace] || 'Workspace';

  const closeModal = openModal({
    title: `🔒 ${targetName} PIN Authorization`,
    subtitle: `Enter ${targetName} PIN to access private ${targetName} workspace`,
    width: 'sm',
    bodyHtml: `
      <div style="display:flex;flex-direction:column;gap:14px;padding:4px 0;">
        <div style="background:var(--ess-bg-subtle,#f1f5f9);border:1px solid var(--ess-border,#cbd5e1);border-radius:8px;padding:10px 14px;font-size:12px;color:var(--ess-text-secondary,#475569);line-height:1.4;">
          🛡️ <strong>Private Account Security:</strong> Every employee account is private. Entering this workspace requires the PIN of an authorized ${targetName}.
        </div>

        <div>
          <label style="display:block;font-size:12px;font-weight:700;margin-bottom:6px;color:var(--ess-text-primary,#0f172a);">Authorizing ${targetName}</label>
          <select id="pmp-staff-select" class="ess-input" style="width:100%;height:38px;padding:6px 10px;border-radius:6px;font-size:13px;">
            ${candidates
              .map(
                (c) =>
                  `<option value="${c.id}" ${c.id === selectedUserId ? 'selected' : ''}>${c.firstName || ''} ${
                    c.lastName || ''
                  } (${Array.isArray(c.roles) ? c.roles.map((r: any) => typeof r === 'string' ? r : r.role).join(', ') : 'Staff'})</option>`
              )
              .join('')}
          </select>
        </div>

        <div style="text-align:center;">
          <label style="display:block;font-size:12px;font-weight:700;margin-bottom:6px;color:var(--ess-text-primary,#0f172a);">Enter 4-Digit Security PIN</label>
          <div id="pmp-pin-display" style="font-size:28px;letter-spacing:10px;font-weight:700;height:44px;display:flex;align-items:center;justify-content:center;background:var(--ess-bg-surface,#fff);border:2px solid var(--ess-primary,#2563eb);border-radius:8px;color:var(--ess-primary,#2563eb);">
            ••••
          </div>
          <div id="pmp-pin-error" style="color:var(--ess-danger,#ef4444);font-size:12px;font-weight:600;min-height:18px;margin-top:6px;"></div>
        </div>

        <!-- Numeric Touch Keypad -->
        <div style="display:grid;grid-template-columns:repeat(3, 1fr);gap:8px;">
          ${[1, 2, 3, 4, 5, 6, 7, 8, 9]
            .map(
              (n) =>
                `<button type="button" class="ess-btn ess-btn-secondary pmp-key" data-val="${n}" style="height:48px;font-size:18px;font-weight:700;">${n}</button>`
            )
            .join('')}
          <button type="button" class="ess-btn ess-btn-secondary pmp-key" data-val="C" style="height:48px;font-size:14px;font-weight:700;color:var(--ess-danger,#ef4444);">CLEAR</button>
          <button type="button" class="ess-btn ess-btn-secondary pmp-key" data-val="0" style="height:48px;font-size:18px;font-weight:700;">0</button>
          <button type="button" class="ess-btn ess-btn-secondary pmp-key" data-val="DEL" style="height:48px;font-size:16px;font-weight:700;">⌫</button>
        </div>
      </div>
    `,
    footerHtml: `
      <div style="display:flex;justify-content:space-between;width:100%;gap:10px;">
        <button type="button" class="ess-btn ess-btn-secondary" id="pmp-cancel-btn" style="flex:1;">Cancel</button>
        <button type="button" class="ess-btn ess-btn-primary" id="pmp-unlock-btn" style="flex:2;background:var(--ess-primary,#2563eb);color:#fff;">Unlock Workspace</button>
      </div>
    `,
    onMount: (modalRoot, close) => {
      const pinDisplay = modalRoot.querySelector('#pmp-pin-display') as HTMLElement;
      const errorDiv = modalRoot.querySelector('#pmp-pin-error') as HTMLElement;
      const staffSelect = modalRoot.querySelector('#pmp-staff-select') as HTMLSelectElement;
      const unlockBtn = modalRoot.querySelector('#pmp-unlock-btn') as HTMLButtonElement;
      const cancelBtn = modalRoot.querySelector('#pmp-cancel-btn') as HTMLButtonElement;

      if (staffSelect) {
        staffSelect.addEventListener('change', () => {
          selectedUserId = staffSelect.value;
          enteredPin = '';
          updateDisplay();
        });
      }

      const updateDisplay = () => {
        if (!pinDisplay) return;
        if (enteredPin.length === 0) {
          pinDisplay.textContent = '••••';
          pinDisplay.style.color = 'var(--ess-text-muted,#94a3b8)';
        } else {
          pinDisplay.textContent = '●'.repeat(enteredPin.length);
          pinDisplay.style.color = 'var(--ess-primary,#2563eb)';
        }
      };
      updateDisplay();

      const submitPin = async () => {
        if (isSubmitting) return;
        if (enteredPin.length < 4) {
          if (errorDiv) errorDiv.textContent = 'Please enter a 4 to 6-digit PIN';
          return;
        }

        isSubmitting = true;
        if (errorDiv) errorDiv.textContent = 'Verifying PIN...';
        if (unlockBtn) unlockBtn.disabled = true;

        try {
          await apiClient.staffPinLogin({
            tenantId,
            userId: selectedUserId,
            pin: enteredPin,
          });
          showToast({
            title: 'Authorized',
            message: `Switched to ${targetName} workspace`,
            type: 'success',
          });
          window.removeEventListener('keydown', keyHandler);
          close();
          onAuthorized();
        } catch (err: any) {
          isSubmitting = false;
          if (unlockBtn) unlockBtn.disabled = false;
          enteredPin = '';
          updateDisplay();
          if (errorDiv) {
            errorDiv.textContent = err?.message || 'Incorrect PIN. Authorization failed.';
          }
        }
      };

      modalRoot.querySelectorAll('.pmp-key').forEach((btn) => {
        btn.addEventListener('click', () => {
          const val = btn.getAttribute('data-val');
          if (val === 'C') {
            enteredPin = '';
          } else if (val === 'DEL') {
            enteredPin = enteredPin.slice(0, -1);
          } else if (val && enteredPin.length < 6) {
            enteredPin += val;
          }
          if (errorDiv) errorDiv.textContent = '';
          updateDisplay();
        });
      });

      unlockBtn?.addEventListener('click', submitPin);
      cancelBtn?.addEventListener('click', () => {
        window.removeEventListener('keydown', keyHandler);
        close();
        onCancel();
      });

      const keyHandler = (e: KeyboardEvent) => {
        if (e.key >= '0' && e.key <= '9') {
          if (enteredPin.length < 6) {
            enteredPin += e.key;
            updateDisplay();
          }
        } else if (e.key === 'Backspace') {
          enteredPin = enteredPin.slice(0, -1);
          updateDisplay();
        } else if (e.key === 'Enter') {
          submitPin();
        } else if (e.key === 'Escape') {
          window.removeEventListener('keydown', keyHandler);
          close();
          onCancel();
        }
      };
      window.addEventListener('keydown', keyHandler);

      modalRoot.querySelector('#ess-modal-close-x')?.addEventListener('click', () => {
        window.removeEventListener('keydown', keyHandler);
        onCancel();
      });
    },
  });
}


